import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { authClient } from '../lib/auth';

interface TelegramUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  telegramId: string | null;
  telegramUsername: string | null;
  role: string;
}

interface TelegramAuthState {
  user: TelegramUser | null;
  token: string | null;
  isLoading: boolean;
}

interface TelegramAuthContextValue extends TelegramAuthState {
  signInWithLoginButton: () => Promise<void>;
}

const TelegramAuthContext = createContext<TelegramAuthContextValue | null>(
  null
);

export function TelegramAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TelegramAuthState>({
    user: null,
    token: localStorage.getItem('token'),
    isLoading: true,
  });

  // Guard to prevent duplicate session checks (e.g. StrictMode double-mount)
  const hasCheckedSession = useRef(false);

  useEffect(() => {
    if (hasCheckedSession.current) return;
    hasCheckedSession.current = true;

    const checkSessionAndSignIn = async () => {
      try {
        // Check for existing session first
        const session = await authClient.getSession();
        if (session.data?.user) {
          const userData = session.data.user as unknown as TelegramUser;
          setState({
            user: userData,
            token: localStorage.getItem('token'),
            isLoading: false,
          });
          return;
        }

        const result = await authClient.autoSignInFromMiniApp();
        if (result.error) {
          if (import.meta.env.DEV) {
            console.error('Auth error:', result.error.message);
          }
          setState((prev) => ({ ...prev, isLoading: false }));
        } else {
          const data = result.data as { user?: TelegramUser; token?: string };
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
          setState({
            user: data.user ?? null,
            token: data.token ?? null,
            isLoading: false,
          });
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error(
            'Failed to check session or sign-in from Mini App',
            err
          );
        }
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    // Only attempt if running inside a Telegram Mini App
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      checkSessionAndSignIn();
    } else {
      // Not in Telegram Mini App — mark loading as done so dependent queries can proceed
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Login via the Telegram Login Widget is handled by UserLoginModal directly.
  // This stub keeps the interface intact.
  const signInWithLoginButton = useCallback(async () => {
    // no-op: widget auth is managed by UserLoginModal via authClient.initTelegramWidget
  }, []);

  return (
    <TelegramAuthContext.Provider value={{ ...state, signInWithLoginButton }}>
      {children}
    </TelegramAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTelegramAuth = () => {
  const context = useContext(TelegramAuthContext);
  if (!context)
    throw new Error('useTelegramAuth must be used within TelegramAuthProvider');
  return context;
};
